import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * FileDeliveryPanel — CrashClaw SEND_FILE 文件交付卡片
 *
 * Bridge 识别助手消息末尾 SEND_FILE:"/path/to/file" 后读取文件，
 * 通过 CRASHCLAW_FILE_DELIVERY 事件下发文件内容，本组件根据 MIME 类型渲染：
 *   - text/html → iframe 内联预览 + 全屏 + 复制 + 下载
 *   - text/*    → 文本预览（可折叠）+ 复制 + 下载
 *   - 其他（二进制）→ 下载卡片（base64 解码为 Blob 下载）
 *
 * 兼容旧 CRASHCLAW_HTML_PREVIEW 纯 HTML 场景。
 */
import React, { useMemo, useState } from 'react';
import { FileOutlined, RightOutlined, DownOutlined, ExclamationCircleOutlined, ExpandOutlined, CopyOutlined, DownloadOutlined, } from '@ant-design/icons';
import { Button, Modal, Tooltip, message } from 'antd';
// @ts-ignore - SCSS module
import scss from './AiDrawer.scss';
import { ze } from 'utils/zhEn';
import ReactMarkdown from 'react-markdown';
import gfm from 'remark-gfm';
const TEXT_MIME_PREFIXES = ['text/'];
const TEXT_EXTRA_MIMES = ['application/json', 'application/xml', 'application/javascript'];
/** 是否为文本类文件（可 UTF-8 直接展示） */
function isTextMime(mime) {
    if (!mime)
        return false;
    return TEXT_MIME_PREFIXES.some((p) => mime.startsWith(p)) || TEXT_EXTRA_MIMES.includes(mime);
}
/** 是否为 HTML 文件 */
function isHtmlMime(mime) {
    return mime === 'text/html' || mime === 'application/xhtml+xml';
}
/** 是否为 Markdown 文件（从文件后缀判断，兼容 .md/.MD/.markdown） */
function isMarkdownFile(filePath) {
    if (!filePath)
        return false;
    const ext = filePath.split('.').pop()?.toLowerCase();
    return ext === 'md' || ext === 'markdown';
}
/** 从文件路径提取文件名 */
function basename(filePath) {
    if (!filePath)
        return 'file';
    return filePath.split('/').pop() || 'file';
}
const FileDeliveryPanel = ({ fileDelivery }) => {
    const [expanded, setExpanded] = useState(true);
    const [hovered, setHovered] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const title = fileDelivery.title || basename(fileDelivery.filePath) || ze('文件预览', 'File Preview');
    const mime = fileDelivery.mimeType || 'application/octet-stream';
    const isHtml = isHtmlMime(mime);
    const isMarkdown = isMarkdownFile(fileDelivery.filePath);
    const isText = isTextMime(mime);
    const hasError = !!fileDelivery.error;
    const hasContent = (!hasError && (fileDelivery.content != null || fileDelivery.contentB64 != null));
    const canExpand = hasContent && (isHtml || isMarkdown || isText);
    // HTML 内容（文本字符串）
    const htmlContent = useMemo(() => {
        if (!hasContent)
            return '';
        if (isHtml)
            return fileDelivery.content || '';
        return '';
    }, [hasContent, isHtml, fileDelivery.content]);
    // 文本内容（用于文本预览和复制）
    const textContent = useMemo(() => {
        if (!hasContent)
            return '';
        if (fileDelivery.content != null)
            return fileDelivery.content;
        // contentB64 场景理论上不会出现在文本类型，但兜底解码
        if (fileDelivery.contentB64) {
            try {
                return atob(fileDelivery.contentB64);
            }
            catch {
                return '';
            }
        }
        return '';
    }, [hasContent, fileDelivery.content, fileDelivery.contentB64]);
    // 二进制下载 URL（base64 → Blob）
    const downloadUrl = useMemo(() => {
        if (!hasContent || !fileDelivery.contentB64)
            return '';
        try {
            const raw = atob(fileDelivery.contentB64);
            const bytes = new Uint8Array(raw.length);
            for (let i = 0; i < raw.length; i++)
                bytes[i] = raw.charCodeAt(i);
            const blob = new Blob([bytes], { type: mime });
            return URL.createObjectURL(blob);
        }
        catch {
            return '';
        }
    }, [hasContent, fileDelivery.contentB64, mime]);
    // 文本类也构造下载 URL
    const textDownloadUrl = useMemo(() => {
        if (!hasContent || !isText || !textContent)
            return '';
        const blob = new Blob([textContent], { type: mime });
        return URL.createObjectURL(blob);
    }, [hasContent, isText, textContent, mime]);
    const fileName = basename(fileDelivery.filePath);
    const handleCopy = (e) => {
        e.stopPropagation();
        const text = isHtml ? htmlContent : textContent;
        if (!text)
            return;
        navigator.clipboard.writeText(text).then(() => message.success(ze('已复制到剪贴板', 'Copied to clipboard')), () => message.error(ze('复制失败', 'Copy failed')));
    };
    const handleExport = (e) => {
        e.stopPropagation();
        // 二进制：用 downloadUrl；文本：用 textDownloadUrl；HTML：构造 Blob
        let url = downloadUrl || textDownloadUrl;
        if (!url && isHtml && htmlContent) {
            const blob = new Blob([htmlContent], { type: 'text/html;charset=utf-8' });
            url = URL.createObjectURL(blob);
        }
        if (!url)
            return;
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        a.click();
        if (!downloadUrl)
            URL.revokeObjectURL(url); // 二进制 URL 在 unmount 时释放
        message.success(ze('导出成功', 'Exported'));
    };
    // 卸载时释放二进制 Blob URL
    React.useEffect(() => {
        return () => {
            if (downloadUrl)
                URL.revokeObjectURL(downloadUrl);
            if (textDownloadUrl)
                URL.revokeObjectURL(textDownloadUrl);
        };
    }, [downloadUrl, textDownloadUrl]);
    const headerClassName = [
        scss.clawHtmlPreviewHeader,
        expanded ? scss.clawHtmlPreviewHeaderExpanded : '',
    ].filter(Boolean).join(' ');
    return (_jsxs(_Fragment, { children: [_jsxs("div", { className: scss.clawHtmlPreview, children: [_jsxs("div", { className: headerClassName, style: hovered ? { background: '#efeafb', borderColor: '#ddd6f3' } : undefined, onClick: () => canExpand && setExpanded(!expanded), onMouseEnter: () => setHovered(true), onMouseLeave: () => setHovered(false), children: [_jsx(FileOutlined, { className: scss.clawHtmlPreviewIcon }), _jsx("span", { className: scss.clawHtmlPreviewTitle, children: title }), hasError ? (_jsxs("span", { className: `${scss.clawHtmlPreviewStatus} ${scss.clawHtmlPreviewStatusError}`, children: [_jsx(ExclamationCircleOutlined, { style: { fontSize: 10 } }), ze('加载失败', 'Failed')] })) : (_jsx("span", { className: `${scss.clawHtmlPreviewStatus} ${scss.clawHtmlPreviewStatusReady}`, children: ze('已就绪', 'Ready') })), canExpand && (_jsxs("span", { className: scss.clawHtmlPreviewActions, onClick: (e) => e.stopPropagation(), children: [(isHtml || isMarkdown) && (_jsx(Tooltip, { title: ze('全屏查看', 'Fullscreen'), children: _jsx(Button, { type: "text", size: "small", icon: _jsx(ExpandOutlined, {}), onClick: () => setModalOpen(true) }) })), _jsx(Tooltip, { title: ze('复制', 'Copy'), children: _jsx(Button, { type: "text", size: "small", icon: _jsx(CopyOutlined, {}), onClick: handleCopy }) }), _jsx(Tooltip, { title: ze('下载', 'Download'), children: _jsx(Button, { type: "text", size: "small", icon: _jsx(DownloadOutlined, {}), onClick: handleExport }) })] })), canExpand && (expanded
                                ? _jsx(DownOutlined, { className: scss.clawHtmlPreviewChevron })
                                : _jsx(RightOutlined, { className: scss.clawHtmlPreviewChevron }))] }), expanded && (_jsx("div", { className: scss.clawHtmlPreviewBody, children: hasError ? (_jsxs("div", { className: scss.clawHtmlPreviewError, children: [_jsx("div", { className: scss.clawHtmlPreviewErrorPath, children: fileDelivery.filePath }), _jsx("div", { children: fileDelivery.error })] })) : isHtml && htmlContent ? (_jsx("iframe", { className: scss.clawHtmlPreviewFrame, title: title, srcDoc: htmlContent, sandbox: "allow-same-origin" })) : isMarkdown && textContent ? (_jsx("div", { className: scss.clawMdPreviewBody, children: _jsx(ReactMarkdown, { remarkPlugins: [gfm], linkTarget: "_blank", children: textContent }) })) : isText && textContent ? (_jsx("pre", { style: {
                                margin: 0,
                                padding: '12px',
                                maxHeight: '400px',
                                overflow: 'auto',
                                fontSize: '12px',
                                lineHeight: '1.5',
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-all',
                                background: '#f6f6f6',
                                borderRadius: '4px',
                            }, children: textContent })) : hasContent ? (_jsx("div", { style: { padding: '12px', textAlign: 'center' }, children: _jsxs(Button, { type: "primary", icon: _jsx(DownloadOutlined, {}), onClick: handleExport, children: [ze('下载文件', 'Download file'), " (", fileName, ")"] }) })) : null }))] }), (isHtml || isMarkdown) && (_jsxs(Modal, { title: title, open: modalOpen, onCancel: () => setModalOpen(false), footer: null, width: "90%", style: { top: 20 }, bodyStyle: { padding: 0, height: 'calc(100vh - 120px)' }, children: [isHtml && htmlContent && (_jsx("iframe", { srcDoc: htmlContent, title: title, sandbox: "allow-same-origin", style: { width: '100%', height: '100%', border: 'none' } })), isMarkdown && textContent && (_jsx("div", { style: { padding: '24px 32px', height: '100%', overflow: 'auto' }, children: _jsx(ReactMarkdown, { remarkPlugins: [gfm], linkTarget: "_blank", children: textContent }) }))] }))] }));
};
export default FileDeliveryPanel;
